import { useState, useRef, useEffect } from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Link } from "react-router-dom";
import { featured } from "./resourcesData";

function BarChartGraphic() {
  const bars = [38, 58, 46, 78, 64, 100];
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 64, flexShrink: 0 }}>
      {bars.map((h, i) => (
        <div
          key={i}
          style={{
            width: 7,
            height: `${h}%`,
            borderRadius: 3,
            background: "linear-gradient(to top, #1e40af 0%, #60a5fa 100%)",
            opacity: 0.45 + (i / bars.length) * 0.55,
          }}
        />
      ))}
    </div>
  );
}

export default function ResourcesHero({ sections, onSearch, searchQuery }) {
  const [active, setActive] = useState(0);
  const trackRef = useRef(null);
  const hasSearch = searchQuery && searchQuery.trim().length > 0;

  useEffect(() => {
    if (trackRef.current && !hasSearch) {
      trackRef.current.scrollTo({ left: active * trackRef.current.offsetWidth, behavior: "smooth" });
    }
  }, [active, hasSearch]);

  const prev = () => setActive(a => Math.max(0, a - 1));
  const next = () => setActive(a => Math.min(featured.length - 1, a + 1));

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 68;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const handleSearchChange = (e) => {
    onSearch(e.target.value);
  };

  return (
    <section
      style={{
        backgroundColor: "#161616",
        paddingTop: "clamp(108px, 9vw, 148px)",
        paddingBottom: hasSearch ? "clamp(24px, 4vw, 40px)" : "clamp(40px, 5vw, 64px)",
        paddingLeft: "clamp(20px, 5vw, 80px)",
        paddingRight: "clamp(20px, 5vw, 80px)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Hero content */}
        <div className="resources-hero-top">
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: 18, marginTop: 0 }}>Resources</p>
          <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "clamp(34px, 5vw, 56px)", color: "#fff", margin: 0, lineHeight: 1.05, letterSpacing: "-0.03em" }}>Guides for landlords</h1>
          {/* Search bar */}
          <div style={{ marginTop: 28, maxWidth: 480 }}>
            <div style={{ position: "relative" }}>
              <Search size={18} color="rgba(255,255,255,0.5)" style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={{ width: "100%", paddingLeft: 44, paddingRight: 16, paddingBlock: 12, borderRadius: 10, border: "1px solid rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.08)", color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: 14, outline: "none", transition: "border-color 0.2s" }}
                onFocus={e => e.target.style.borderColor = "rgba(255,255,255,0.4)"}
                onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.2)"}
              />
            </div>
          </div>
        </div>

        {/* Featured slider - hidden when searching */}
        {!hasSearch && (
          <div style={{ marginTop: "clamp(40px, 5vw, 60px)" }}>
            <div ref={trackRef} style={{ overflow: "hidden", borderRadius: 18 }}>
              <div style={{ display: "flex" }}>
                {featured.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      minWidth: "100%",
                      display: i === active ? "grid" : "none",
                      gridTemplateColumns: "1fr 1fr",
                      background: "#1f1f1f",
                      borderRadius: 18,
                      overflow: "hidden",
                      height: 380,
                    }}
                    className="featured-card-grid"
                  >
                    <div style={{ padding: "40px", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
                      <div>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: 18, marginTop: 0 }}>{item.tag}</p>
                        <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(20px, 2.4vw, 30px)", lineHeight: 1.2, color: "#fff", marginTop: 0, marginBottom: 16, letterSpacing: "-0.02em" }}>{item.title}</h2>
                        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.65)", lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                      </div>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 28, marginBottom: 18 }}>
                          <img
                            src="https://media.base44.com/images/public/6a316287a1be7d9945ac7d10/52e98417f_Untitleddesign37.jpg"
                            alt="Truestays"
                            style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", background: "#fff", padding: 4 }}
                          />
                          <div>
                            <p style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13, color: "#fff", margin: 0 }}>{item.author.name}</p>
                            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.5)", margin: "2px 0 0" }}>{item.author.role}</p>
                          </div>
                        </div>
                        <Link to={item.href} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, color: "#fff", textDecoration: "none" }}>
                          Read article <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                    <div style={{ overflow: "hidden", height: "100%" }}>
                      <img src={item.img} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {featured.map((_, i) => (
                  <button key={i} onClick={() => setActive(i)} style={{ width: i === active ? 24 : 8, height: 8, borderRadius: 100, background: i === active ? "#fff" : "rgba(255,255,255,0.25)", border: "none", cursor: "pointer", transition: "all 0.25s", padding: 0 }} />
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={prev} disabled={active === 0} style={{ width: 38, height: 38, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.2)", background: "transparent", cursor: active === 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: active === 0 ? 0.4 : 1, transition: "opacity 0.2s" }}>
                  <ChevronLeft size={16} color="#fff" />
                </button>
                <button onClick={next} disabled={active === featured.length - 1} style={{ width: 38, height: 38, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.2)", background: "transparent", cursor: active === featured.length - 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: active === featured.length - 1 ? 0.4 : 1, transition: "opacity 0.2s" }}>
                  <ChevronRight size={16} color="#fff" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .featured-card-grid { grid-template-columns: 1fr !important; height: auto !important; }
          .featured-card-grid > div:first-child { padding: 28px !important; }
          .featured-card-grid > div:last-child { height: 200px !important; }
        }
      `}</style>
    </section>
  );
}